import React, { useState, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { formatDate } from '../utils/dateFormatter';
import html2pdf from 'html2pdf.js';
import { sendQuoteEmail } from '../utils/quoteUtils';
import { SIZES } from '../firebase/schema';
import { groupDocumentLinesByType } from '../utils/documentLineGroups';
import { computeOrderDocumentTotals, getOrderFees } from '../utils/orderTotals';

const QuoteWrapper = styled.div`
  background: white;
  color: black;
  padding: 20px 25px;
  max-width: 8.5in;
  margin: 0 auto;
  font-family: 'Montserrat', Arial, sans-serif;
  text-transform: uppercase;
  
  /* Page break handling for PDF generation */
  @media print {
    padding: 0;
    max-width: 100%;
  }
  
  @page {
    size: letter;
    margin: 0.25in 0.35in;
  }
`;

const QuoteHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  align-items: center;
  margin-bottom: 40px;
  page-break-inside: avoid;
  page-break-after: avoid;
`;

const BlackBox = styled.div`
  background: black;
  color: white;
  padding: 15px 25px;
  font-size: 1.2em;
  letter-spacing: 0.05em;
  
  &.left {
    text-align: left;
  }
  
  &.right {
    text-align: right;
  }

  &.footer-total {
    margin-top: 20px;
    display: inline-block;
    float: right;
  }
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    height: 80px;
    width: auto;
  }
`;

const InfoSection = styled.div`
  margin-bottom: 30px;
  page-break-inside: avoid;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-bottom: 20px;
  page-break-inside: avoid;
  align-items: stretch;
`;

const InfoColumn = styled.div`
  display: flex;
  flex-direction: column;
  
  .label {
    font-size: 0.8em;
    text-transform: uppercase;
    margin-bottom: 5px;
    font-weight: 600;
  }
  
  .value {
    font-size: 1em;
    border-bottom: 1px solid black;
    padding-bottom: 5px;
    flex: 1;
    display: flex;
    align-items: flex-end;
  }
`;

const EmailRow = styled.div`
  text-align: left;
  font-size: 0.9em;
  margin: 20px 0;
  color: #333;
`;

const TypeSection = styled.div`
  margin-top: 20px;

  &:first-of-type {
    margin-top: 8px;
  }
`;

const TypeSectionTitle = styled.h3`
  margin: 0 0 4px;
  padding: 8px 12px;
  background: black;
  color: white;
  font-size: 0.75em;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  page-break-after: avoid;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: 20px 0;
  page-break-inside: auto;
  
  thead {
    display: table-header-group;
  }
  
  tbody {
    display: table-row-group;
  }
  
  th {
    border-bottom: 1px solid black;
    text-align: left;
    padding: 6px 3px;
    font-size: 0.65em;
    text-transform: uppercase;
    font-weight: 600;
    white-space: nowrap;
    
    &.size-header {
      text-align: center;
      padding: 2px 3px;
      font-size: 0.6em;
    }
  }
  
  tr {
    page-break-inside: avoid;
    page-break-after: auto;
  }
  
  td {
    padding: 6px 3px;
    border-bottom: 1px solid #eee;
    font-size: 0.65em;
    
    &.size-cell {
      text-align: center;
      width: 30px;
    }
    
    &.number-cell {
      text-align: right;
    }
  }
`;

const SizeHeadersRow = styled.tr`
  th {
    text-align: center;
    border-bottom: none;
    padding-bottom: 0;
  }
`;

const SizeSubHeadersRow = styled.tr`
  th {
    text-align: center;
    font-size: 0.55em;
    font-weight: normal;
    color: #666;
    padding-top: 0;
  }
`;

const Totals = styled.div`
  margin-top: 30px;
  page-break-inside: avoid;
  
  .row {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    margin: 5px 0;
    gap: 20px;
    
    .label {
      text-transform: uppercase;
      font-size: 0.9em;
      font-weight: 500;
      text-align: right;
      min-width: 120px;
    }
    
    .value {
      text-align: right;
      min-width: 100px;
    }
  }
`;

const WaivedBox = styled.span`
  background: black;
  color: white;
  padding: 2px 16px;
  font-size: 0.8em;
  text-transform: uppercase;
  letter-spacing: 0.1em;
`;

const Footer = styled.div`
  margin-top: 60px;
  border-top: 1px solid black;
  padding-top: 20px;
  page-break-inside: avoid;
  
  .quote-validity {
    text-transform: uppercase;
    font-weight: 600;
    font-size: 0.9em;
    margin-bottom: 10px;
  }
  
  .quote-text {
    font-size: 0.8em;
    color: #666;
    margin-bottom: 20px;
    text-transform: none; /* Allow mixed case for readability */
  }
  
  .address {
    margin-top: 10px;
    font-size: 0.8em;
    display: inline-block;
  }
`;

const sortLineItems = (items) => {
  return [...items].sort((a, b) => {
    const descA = (a.designName || '').toString().trim().toLowerCase();
    const descB = (b.designName || '').toString().trim().toLowerCase();
    if (descA !== descB) return descA.localeCompare(descB);
    const styleA = (a.garmentId || a.styleNo || '').toString().trim().toLowerCase();
    const styleB = (b.garmentId || b.styleNo || '').toString().trim().toLowerCase();
    if (styleA !== styleB) return styleA.localeCompare(styleB);
    const colorA = (a.garmentColor || '').toString().trim().toLowerCase();
    const colorB = (b.garmentColor || '').toString().trim().toLowerCase();
    return colorA.localeCompare(colorB);
  });
};

const ActionButtons = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 24px;
  justify-content: flex-end;
`;

const Button = styled.button`
  background: ${props => props.primary ? 'var(--al-primary)' : 'transparent'};
  color: ${props => props.primary ? '#222' : 'var(--al-primary)'};
  border: 1px solid var(--al-primary);
  padding: 8px 16px;
  font-family: 'Montserrat', sans-serif;
  font-size: 0.9em;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.primary ? '#7AE07A' : 'rgba(var(--al-primary-rgb), 0.1)'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Quote = ({ quote, order, onUpdate, hideActions = false, type = 'quote' }) => {
  // Support both quote and order data - normalize to common format
  const data = quote || order || {};
  const isQuote = type === 'quote';
  const documentNumber = isQuote 
    ? data.quoteNumber 
    : (data.poNumber || data.orderNumber || data.id?.slice(0, 8));
  const [lineItems, setLineItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [sending, setSending] = useState(false);
  const quoteRef = useRef(null);

  const documentFees = getOrderFees(data);

  useEffect(() => {
    const fetchLineItems = async () => {
      try {
        // Fetch from appropriate collection based on type
        const collectionName = isQuote ? 'quoteLines' : 'orderLines';
        const idField = isQuote ? 'quoteId' : 'orderId';
        
        const q = query(
          collection(db, collectionName),
          where(idField, '==', data.id)
        );
        const querySnapshot = await getDocs(q);
        const items = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setLineItems(items);
      } catch (error) {
        console.error('Error fetching line items:', error);
      } finally {
        setLoading(false);
      }
    };
    if (data?.id) {
      fetchLineItems();
    }
  }, [data?.id, isQuote]);

  const totals = computeOrderDocumentTotals(data, lineItems, { fees: documentFees });

  const quoteSections = groupDocumentLinesByType(lineItems);

  const handleExportPDF = async () => {
    if (!quoteRef.current) return;
    
    setExporting(true);
    try {
      const element = quoteRef.current;
      const filename = isQuote 
        ? `Quote_${documentNumber}.pdf`
        : `Order_${documentNumber}.pdf`;
      const opt = {
        margin: [0.25, 0.35, 0.25, 0.35], // top, left, bottom, right in inches
        filename,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          letterRendering: true
        },
        jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
      };

      const pdf = await html2pdf().set(opt).from(element).output('blob');
      
      // Save PDF locally
      const url = window.URL.createObjectURL(pdf);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Quote_${quote.quoteNumber}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      
      alert('Quote exported successfully!');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  const handleEmailQuote = async () => {
    if (!data.contactEmail) {
      alert('No email address available for this customer');
      return;
    }

    setSending(true);
    try {
      const element = quoteRef.current;
      const filename = isQuote 
        ? `Quote_${documentNumber}.pdf`
        : `Order_${documentNumber}.pdf`;
      const opt = {
        margin: [0.25, 0.35, 0.25, 0.35], // top, left, bottom, right in inches
        filename,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          letterRendering: true
        },
        jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
      };

      const pdfBlob = await html2pdf().set(opt).from(element).output('blob');
      
      const reader = new FileReader();
      const pdfBase64 = await new Promise((resolve) => {
        reader.onloadend = () => {
          const base64String = reader.result.split(',')[1];
          resolve(base64String);
        };
        reader.readAsDataURL(pdfBlob);
      });
      
      await sendQuoteEmail({
        to: data.contactEmail,
        customerName: data.customerName || data.client,
        quoteNumber: documentNumber,
        pdfContent: pdfBase64,
        createdAt: formatDate(data.createdAt)
      });
      
      alert('Quote sent successfully!');
    } catch (error) {
      console.error('Error sending quote:', error);
      alert('Failed to send email');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return <div>Loading quote details...</div>;
  }

  return (
    <div>
      <QuoteWrapper ref={quoteRef}>
        <QuoteHeader>
          <BlackBox className="left">
            {isQuote ? 'QUOTE NO.' : 'ORDER NO.'}<br />
            {documentNumber}
          </BlackBox>
          <Logo><img src="/images/87c-id-144x144.png" alt="87 Central" /></Logo>
          <BlackBox className="right">
            TOTAL<br />
            ${totals.total.toFixed(2)}
          </BlackBox>
        </QuoteHeader>

        <InfoGrid>
          <InfoColumn>
            <div className="label">Client</div>
            <div className="value">{data.client || data.customerName}</div>
          </InfoColumn>
          <InfoColumn>
            <div className="label">Contact</div>
            <div className="value">{data.contact}</div>
          </InfoColumn>
          <InfoColumn>
            <div className="label">Manager</div>
            <div className="value">{data.projectManager}</div>
          </InfoColumn>
          <InfoColumn>
            <div className="label">Date</div>
            <div className="value">{formatDate(data.createdAt)}</div>
          </InfoColumn>
        </InfoGrid>

        {data.contactEmail && (
          <EmailRow>{data.contactEmail}</EmailRow>
        )}

        {quoteSections.map(({ lineType, sizeGroups }) => (
          <TypeSection key={lineType}>
            <TypeSectionTitle>{lineType}</TypeSectionTitle>
            {sizeGroups.map(({ sizeType, lines: groupLines }) => {
              const sizesForType = SIZES[sizeType] || [];
              const itemsInGroup = sortLineItems(groupLines);

              return (
                <Table key={`${lineType}-${sizeType}`}>
                  <thead>
                    <tr>
                      <th>Description</th>
                      <th>Style</th>
                      <th>Color</th>
                      {sizesForType.map(size => (
                        <th key={size} className="size-header">{size}</th>
                      ))}
                      <th style={{ textAlign: 'right' }}>QTY</th>
                      <th style={{ textAlign: 'right' }}>RATE</th>
                      <th style={{ textAlign: 'right' }}>AMOUNT</th>
                    </tr>
                  </thead>
                  <tbody>
                    {itemsInGroup.map(line => {
                      const styleNo = line.styleNo || line.garmentId?.split(' - ')[0] || '';
                      const styleDisplay = line.brand
                        ? `${line.brand} ${styleNo}`.trim()
                        : styleNo;

                      return (
                        <tr key={line.id}>
                          <td>{line.designName}</td>
                          <td>{styleDisplay}</td>
                          <td>{line.garmentColor}</td>
                          {sizesForType.map(size => (
                            <td key={size} className="size-cell">
                              {line.sizes?.[size] > 0 ? line.sizes[size] : ''}
                            </td>
                          ))}
                          <td className="number-cell">{line.totalQuantity}</td>
                          <td className="number-cell">${parseFloat(line.pricePerPiece || 0).toFixed(2)}</td>
                          <td className="number-cell">
                            ${(line.totalQuantity * (parseFloat(line.pricePerPiece) || 0)).toFixed(2)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              );
            })}
          </TypeSection>
        ))}

        <Totals>
          <div className="row">
            <div className="label">Sub Total</div>
            <div className="value">${totals.subtotal.toFixed(2)}</div>
          </div>

          {/* 2XL+ upcharge info (included in subtotal) */}
          {totals.upchargeTotal > 0 && (
            <div className="row" style={{ fontSize: '0.85em', color: '#666' }}>
              <div className="label">(Includes 2XL+ Upcharge)</div>
              <div className="value">${totals.upchargeTotal.toFixed(2)}</div>
            </div>
          )}

          {/* Discount, if any */}
          {totals.discountAmount > 0 && (
            <div className="row">
              <div className="label">
                Discount {data.discount?.type === 'percent' ? `(${data.discount.amount}%)` : ''}
              </div>
              <div className="value" style={{ color: '#dc3545' }}>
                -${totals.discountAmount.toFixed(2)}
              </div>
            </div>
          )}

          {/* Fees (Artwork/Setup/etc.) */}
          {documentFees.length > 0 ? (
            documentFees.map((fee, index) => {
              const quantity = parseFloat(fee.quantity) || 0;
              const price = parseFloat(fee.price) || 0;
              const feeTotal = quantity * price;
              return (
                <div className="row" key={fee.id || index}>
                  <div className="label">{fee.type}</div>
                  <div className="value">${feeTotal.toFixed(2)}</div>
                </div>
              );
            })
          ) : (
            <>
              <div className="row">
                <div className="label">Artwork Fee</div>
                <div><WaivedBox>Waived</WaivedBox></div>
                <div className="value">$0.00</div>
              </div>
              <div className="row">
                <div className="label">Setup Fee</div>
                <div><WaivedBox>Waived</WaivedBox></div>
                <div className="value">$0.00</div>
              </div>
            </>
          )}

          {/* Sales tax, honoring tax-exempt flag */}
          <div className="row">
            <div className="label">Sales Tax</div>
            {totals.isTaxExempt && <div><WaivedBox>Tax Exempt</WaivedBox></div>}
            <div className="value">${totals.tax.toFixed(2)}</div>
          </div>
        </Totals>

        <Footer>
          {isQuote ? (
            <>
              <div className="quote-validity">Quote Valid For 30 Days</div>
              <div className="quote-text">
                This quote is valid for 30 days from {formatDate(data.createdAt)}.<br />
                Pricing is based on the quantities specified. Changes in quantities may affect pricing.<br />
                Please note we accept all major credit cards. To proceed with this quote, please contact:
              </div>
            </>
          ) : (
            <>
              <div className="quote-validity">Order Confirmation</div>
              <div className="quote-text">
                This document is a summary of order #{documentNumber}.<br />
                For questions about this order, please contact your project manager.<br />
                Please note we accept all major credit cards. Please make checks payable to:
              </div>
            </>
          )}
          <div className="address">
            Eight Seven Central<br />
            863 42nd Street<br />
            Des Moines, IA 50312
          </div>
          <BlackBox className="footer-total">
            TOTAL<br />
            ${totals.total.toFixed(2)}
          </BlackBox>
        </Footer>
      </QuoteWrapper>

      {!hideActions && (
        <ActionButtons>
          <Button 
            onClick={handleExportPDF}
            disabled={exporting}
          >
            {exporting ? 'Exporting...' : 'Export PDF'}
          </Button>
          <Button 
            primary
            onClick={handleEmailQuote}
            disabled={sending || !data.contactEmail}
          >
            {sending ? 'Sending...' : 'Email Quote'}
          </Button>
        </ActionButtons>
      )}
    </div>
  );
};

export default Quote; 